"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function SparkleIcon({ size = 16, color = PURPLE }: { size?: number; color?: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" fill={color} />
        </svg>
    );
}

/* concentric arcs radiating from the panel, echoing an "answer ripple" */
function ArcBackground() {
    const radii = [90, 150, 210, 270];
    return (
        <svg
            className="absolute pointer-events-none"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            width="640"
            height="640"
            viewBox="0 0 640 640"
            fill="none"
            aria-hidden="true"
        >
            {radii.map((r, i) => (
                <circle
                    key={r}
                    cx="320"
                    cy="320"
                    r={r}
                    stroke={i % 2 === 0 ? PURPLE : LIME}
                    strokeOpacity={0.14 - i * 0.02}
                    strokeWidth="1.4"
                    fill="none"
                />
            ))}
        </svg>
    );
}

interface CitationBadge {
    label: string;
    style: React.CSSProperties;
    rotate: number;
    delay: number;
}

const badges: CitationBadge[] = [
    { label: 'Cited in AI Overviews', style: { top: -54, left: -28 }, rotate: -4, delay: 0.9 },
    { label: 'Referenced by ChatGPT', style: { bottom: -26, right: -24 }, rotate: 3, delay: 1.1 },
];

function CitationBadgeChip({ badge }: { badge: CitationBadge }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: badge.delay }}
            className="hidden md:flex absolute items-center gap-2 pl-3 pr-3.5 py-2 rounded-full"
            style={{
                ...badge.style,
                background: CARD,
                boxShadow: '0 14px 30px -12px rgba(8,4,0,0.22)',
                border: '1px solid rgba(8,4,0,0.06)',
                transform: `rotate(${badge.rotate}deg)`,
                zIndex: 5,
            }}
        >
            <SparkleIcon size={13} />
            <span className="text-xs font-medium whitespace-nowrap" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.75)' }}>
                {badge.label}
            </span>
        </motion.div>
    );
}

const sources = [
    { label: 'ChatGPT', color: '#10a37f' },
    { label: 'Gemini', color: '#4285f4' },
    { label: 'Perplexity', color: '#20808d' },
];

function AIAnswerPanel() {
    return (
        <div className="relative w-full max-w-[380px] mx-auto lg:mx-0">
            <ArcBackground />

            {/* washi tape */}
            <div
                className="absolute -top-4 left-10 w-16 h-7 pointer-events-none"
                style={{ background: 'rgba(206,255,26,0.55)', transform: 'rotate(-6deg)', boxShadow: '0 2px 6px rgba(8,4,0,0.08)', zIndex: 6 }}
            />

            <motion.div
                initial={{ opacity: 0, y: 24, rotate: 0 }}
                whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.35, ease: 'easeOut' }}
                className="relative rounded-[22px] p-5"
                style={{ background: CARD, border: '1.5px solid rgba(8,4,0,0.1)', boxShadow: '0 24px 60px -20px rgba(8,4,0,0.25)', zIndex: 4 }}
            >
                {/* letterhead */}
                <div className="flex items-center justify-between pb-4 mb-4" style={{ borderBottom: '1px dashed rgba(8,4,0,0.14)' }}>
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ background: INK }}
                        >
                            <SparkleIcon size={16} color={LIME} />
                        </div>
                        <div className="flex flex-col leading-none">
                            <span className="text-xs font-bold" style={{ fontFamily: CLASH, color: INK }}>AI Answer</span>
                            <span className="text-[11px] font-mono mt-0.5" style={{ color: 'rgba(8,4,0,0.4)' }}>generated summary</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <motion.span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: PURPLE }}
                            animate={{ opacity: [1, 0.35, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: 'rgba(8,4,0,0.4)' }}>live</span>
                    </div>
                </div>

                {/* mock answer copy */}
                <div className="rounded-xl p-3.5 mb-4" style={{ background: 'rgba(55,48,163,0.05)', border: '1px solid rgba(55,48,163,0.12)' }}>
                    <p className="text-[13px] leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.75)' }}>
                        Based on structured data and site content,{' '}
                        <span className="font-semibold" style={{ color: PURPLE }}>Lead Workz</span>{' '}
                        is a performance marketing and SEO agency specialising in AI-search visibility.
                    </p>
                </div>

                {/* citation row */}
                <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.35)' }}>
                        Referenced across
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {sources.map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, x: -8 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.6 + i * 0.08 }}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                                style={{ background: 'rgba(8,4,0,0.04)', border: '1px solid rgba(8,4,0,0.08)' }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                                <span className="text-[11px] font-medium" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)' }}>
                                    {s.label}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {badges.map((b) => (
                <CitationBadgeChip key={b.label} badge={b} />
            ))}

            {/* mock AI-search bar */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.3 }}
                className="relative flex items-center gap-3 rounded-full pl-5 pr-4 py-3.5 mt-6"
                style={{ background: INK, zIndex: 4 }}
            >
                <SparkleIcon size={16} color={LIME} />
                <span className="flex-1 text-left text-sm" style={{ fontFamily: MANROPE, color: '#fbfdfd' }}>
                    how does my brand show up in AI search
                    <motion.span
                        aria-hidden="true"
                        className="inline-block w-[1.5px] h-4 ml-0.5 align-middle"
                        style={{ background: LIME }}
                        animate={{ opacity: [1, 0, 1] }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                    />
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" stroke={LIME} strokeWidth="2" />
                    <path d="M20 20l-4.5-4.5" stroke={LIME} strokeWidth="2" strokeLinecap="round" />
                </svg>
            </motion.div>
        </div>
    );
}

export default function AISEOHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-36 pb-24 overflow-hidden"
                style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
            >
                {/* dot grid texture, matches the dark sections below */}
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
                />

                {/* single asymmetric wash */}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: '-18%', right: '-10%', width: 620, height: 560,
                        background: 'radial-gradient(circle, rgba(55,48,163,0.12) 0%, transparent 68%)',
                        borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                    }}
                />

                <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                    {/* Left: copy */}
                    <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">

                        {/* Breadcrumb */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/search-visibility" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                SEO Services
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>AI SEO &amp; GEO</span>
                        </motion.div>

                        {/* Eyebrow, stamp-style */}
                        <motion.p
                            initial={{ opacity: 0, y: 14, rotate: 0 }}
                            animate={{ opacity: 1, y: 0, rotate: -2 }}
                            transition={{ duration: 0.5, delay: 0.05 }}
                            className="text-xs md:text-sm font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                            style={{ color: PURPLE, fontFamily: CLASH, border: `1.5px solid ${PURPLE}` }}
                        >
                            AI SEO &amp; GEO
                        </motion.p>

                        {/* H1 */}
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            Make Your Brand Easier for{' '}
                            <span style={{ color: PURPLE }}>AI Search</span>{' '}
                            to Understand
                        </motion.h1>

                        {/* Description */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-4 max-w-xl"
                        >
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Search is becoming more conversational and increasingly driven by AI. People aren&apos;t searching for a list of websites anymore — they&apos;re asking questions and expecting useful answers.
                            </p>
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Lead Workz structures your website, content and brand information so modern search systems can understand, retrieve and reference it — building the clarity and technical foundation that makes your brand discoverable across evolving search experiences.
                            </p>
                        </motion.div>

                        {/* CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.32 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                                style={{ background: PURPLE, color: '#ffffff', fontFamily: CLASH, letterSpacing: '0.08em' }}
                            >
                                Get an AI Search Audit →
                            </button>
                        </motion.div>

                        {/* Google guidance footnote */}
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="max-w-lg text-xs md:text-sm leading-relaxed pt-1"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.4)', letterSpacing: '0.01em' }}
                        >
                            Google&apos;s current guidance confirms that AI Overviews and AI Mode continue to rely on core Search systems and foundational SEO practices.
                        </motion.p>
                    </div>

                    {/* Right: AI answer panel illustration */}
                    <div className="lg:col-span-5">
                        <AIAnswerPanel />
                    </div>

                </div>
            </div>
        </section>
    );
}
